/**
 * Text Sanitization Utility
 * 
 * Sanitizes text to prevent encoding issues when sending to APIs
 * Replaces problematic Unicode characters with ASCII equivalents
 */

/**
 * Sanitizes text by replacing special Unicode characters with ASCII equivalents
 * @param {string} text - Text to sanitize
 * @returns {string} - Sanitized text
 */
export const sanitizeText = (text) => {
    if (!text || typeof text !== 'string') {
        return text;
    }

    return text
        // Smart quotes
        .replace(/['']/g, "'")  // Left/Right single quotation mark → regular apostrophe
        .replace(/[""]/g, '"')  // Left/Right double quotation mark → regular quote
        
        // Dashes
        .replace(/—/g, '-')     // Em dash → hyphen
        .replace(/–/g, '-')      // En dash → hyphen
        
        // Other common problematic characters
        .replace(/…/g, '...')    // Ellipsis → three dots
        .replace(/•/g, '*')      // Bullet → asterisk
        .replace(/°/g, 'deg')    // Degree symbol → deg
        .replace(/©/g, '(c)')    // Copyright → (c)
        .replace(/®/g, '(R)')     // Registered → (R)
        .replace(/™/g, '(TM)')   // Trademark → (TM)
        
        // Non-breaking spaces
        .replace(/\u00A0/g, ' ') // Non-breaking space → regular space
        
        // Zero-width characters
        .replace(/[\u200B-\u200D\uFEFF]/g, ''); // Zero-width spaces/joiner → remove
};

/**
 * Sanitizes property data before submission
 * @param {Object} propertyData - Property data object
 * @returns {Object} - Sanitized property data
 */
export const sanitizePropertyData = (propertyData) => {
    if (!propertyData || typeof propertyData !== 'object') {
        return propertyData;
    }

    const sanitized = { ...propertyData };

    // Fields that commonly contain text that needs sanitization
    const textFields = [
        'title',
        'headline',
        'description',
        'address',
        'street',
        'suburb',
        'city',
        'features',
        'other_info',
        'auction_venue',
        'owner_name',
        'owner_contact',
        'owner_email'
    ];

    textFields.forEach(field => {
        if (sanitized[field] && typeof sanitized[field] === 'string') {
            sanitized[field] = sanitizeText(sanitized[field]);
        }
    });

    // Handle nested objects
    if (sanitized.extra_info && typeof sanitized.extra_info === 'object') {
        const extraInfo = { ...sanitized.extra_info };
        textFields.forEach(field => {
            if (extraInfo[field] && typeof extraInfo[field] === 'string') {
                extraInfo[field] = sanitizeText(extraInfo[field]);
            }
        });
        sanitized.extra_info = extraInfo;
    }

    // Handle owner object
    if (sanitized.owner && typeof sanitized.owner === 'object') {
        sanitized.owner = {
            ...sanitized.owner,
            name: sanitized.owner.name ? sanitizeText(sanitized.owner.name) : sanitized.owner.name,
            contact: sanitized.owner.contact ? sanitizeText(sanitized.owner.contact) : sanitized.owner.contact,
            email: sanitized.owner.email ? sanitizeText(sanitized.owner.email) : sanitized.owner.email
        };
    }

    return sanitized;
};

/**
 * Checks if text contains problematic characters
 * @param {string} text - Text to check
 * @returns {boolean} - True if text contains problematic characters
 */
export const hasProblematicCharacters = (text) => {
    if (!text || typeof text !== 'string') {
        return false;
    }

    // Check for common problematic Unicode characters
    const problematicPattern = /[''""—–…•°©®™\u00A0\u200B-\u200D\uFEFF]/;
    return problematicPattern.test(text);
};
